import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { useGetEcommerceDashboardQuery } from "slices/addOnApi";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LayersIcon from "@mui/icons-material/Layers";

ChartJS.register(
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
);

// ── helpers ───────────────────────────────────────────────────
const SC = ({ children }) => (
  <Typography
    variant="h6"
    fontWeight={700}
    mb={2}
    sx={{ color: "#442141", borderLeft: "4px solid #6C63FF", pl: 1.5 }}
  >
    {children}
  </Typography>
);

const KpiCard = ({
  icon,
  label,
  value,
  sub,
  subColor = "#22c55e",
  bg = "#fff",
}) => (
  <Card
    sx={{
      p: 2.5,
      borderRadius: 3,
      height: "100%",
      background: bg,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing={1}
        >
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>
          {value ?? 0}
        </Typography>
        {sub && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <TrendingUpIcon sx={{ fontSize: 13, color: subColor }} />
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: subColor }}
            >
              {sub}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          background: "rgba(108,99,255,0.12)",
          borderRadius: 2,
          p: 1.2,
          display: "flex",
        }}
      >
        {icon}
      </Box>
    </Box>
  </Card>
);

const statusColorMap = {
  processing: "warning",
  shipped: "info",
  delivered: "success",
  cancelled: "error",
  pending: "warning",
  paid: "success",
  failed: "error",
  cod: "default",
  online: "primary",
};
const StatusChip = (s) => (
  <Chip
    label={s}
    size="small"
    color={statusColorMap[s] || "default"}
    sx={{ fontWeight: 600, fontSize: 11 }}
  />
);

const TH = ({ cols }) => (
  <TableHead>
    <TableRow sx={{ background: "#f0eeff" }}>
      {cols.map((c) => (
        <TableCell
          key={c}
          sx={{
            fontWeight: 700,
            fontSize: 12,
            color: "#442141",
            whiteSpace: "nowrap",
            px: 2,
            py: 1,
          }}
        >
          {c}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const chartOpts = (theme) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: { color: theme.palette.text.primary, font: { size: 11 } },
    },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: {
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
    y: {
      beginAtZero: true,
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
  },
});

// ── component ─────────────────────────────────────────────────
function EcommerceDashboard() {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetEcommerceDashboardQuery();

  if (isLoading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress sx={{ color: "#6C63FF" }} />
        </Box>
        <Footer />
      </DashboardLayout>
    );

  if (isError || !data)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <Typography color="error">
            Failed to load e-commerce dashboard.
          </Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );

  const {
    catalogCounts,
    orderSummary,
    monthlyEarnings,
    monthlyStatusBreakdown,
    productStockTable,
    latestOrders,
    categoryProductCount,
  } = data;

  // ── chart data ──────────────────────────────────────────────
  const months = monthlyEarnings?.map((d) => d.month) || [];

  const earningLineData = {
    labels: months,
    datasets: [
      {
        label: "Monthly Earning (₹)",
        data: monthlyEarnings?.map((d) => d.totalEarning) || [],
        borderColor: "#6C63FF",
        backgroundColor: "rgba(108,99,255,0.15)",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#6C63FF",
      },
      {
        label: "Order Count",
        data: monthlyEarnings?.map((d) => d.orderCount) || [],
        borderColor: "#FF6584",
        backgroundColor: "rgba(255,101,132,0.1)",
        fill: false,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#FF6584",
        yAxisID: "y1",
      },
    ],
  };

  const earningLineOpts = {
    ...chartOpts(theme),
    scales: {
      ...chartOpts(theme).scales,
      y1: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        ticks: { color: "#FF6584", font: { size: 10 } },
        grid: { drawOnChartArea: false },
      },
    },
  };

  const statusBarData = {
    labels: monthlyStatusBreakdown?.map((d) => d.month) || [],
    datasets: [
      {
        label: "Processing",
        data: monthlyStatusBreakdown?.map((d) => d.processing) || [],
        backgroundColor: "rgba(245,158,11,0.75)",
        borderRadius: 3,
      },
      {
        label: "Shipped",
        data: monthlyStatusBreakdown?.map((d) => d.shipped) || [],
        backgroundColor: "rgba(59,130,246,0.75)",
        borderRadius: 3,
      },
      {
        label: "Delivered",
        data: monthlyStatusBreakdown?.map((d) => d.delivered) || [],
        backgroundColor: "rgba(34,197,94,0.75)",
        borderRadius: 3,
      },
      {
        label: "Cancelled",
        data: monthlyStatusBreakdown?.map((d) => d.cancelled) || [],
        backgroundColor: "rgba(239,68,68,0.75)",
        borderRadius: 3,
      },
    ],
  };

  const orderStatusDoughnutData = {
    labels: ["Processing", "Shipped", "Delivered", "Cancelled"],
    datasets: [
      {
        data: [
          orderSummary?.processing || 0,
          orderSummary?.shipped || 0,
          orderSummary?.delivered || 0,
          orderSummary?.cancelled || 0,
        ],
        backgroundColor: ["#f59e0b", "#3b82f6", "#22c55e", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const paymentDoughnutData = {
    labels: ["COD", "Online"],
    datasets: [
      {
        data: [orderSummary?.cod || 0, orderSummary?.online || 0],
        backgroundColor: ["#6C63FF", "#43C6AC"],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } },
  };

  // max stock for progress bar
  const maxStock = Math.max(
    ...(productStockTable?.map((p) => p.totalStock) || [1]),
    1,
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ── KPI ROW 1 — Catalog ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            {
              icon: <CategoryIcon sx={{ color: "#6C63FF", fontSize: 26 }} />,
              label: "Shop Categories",
              value: catalogCounts?.categories?.toLocaleString(),
            },
            {
              icon: <LayersIcon sx={{ color: "#FF6584", fontSize: 26 }} />,
              label: "Sub-Categories",
              value: catalogCounts?.subCategories?.toLocaleString(),
            },
            {
              icon: <InventoryIcon sx={{ color: "#43C6AC", fontSize: 26 }} />,
              label: "Total Products",
              value: catalogCounts?.totalProducts?.toLocaleString(),
            },
            {
              icon: <StorefrontIcon sx={{ color: "#22c55e", fontSize: 26 }} />,
              label: "Available Products",
              value: catalogCounts?.availableProducts?.toLocaleString(),
              bg: "#f0fdf4",
            },
            {
              icon: <InventoryIcon sx={{ color: "#94a3b8", fontSize: 26 }} />,
              label: "Deleted Products",
              value: catalogCounts?.deletedProducts?.toLocaleString(),
              bg: "#f8fafc",
              subColor: "#94a3b8",
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <KpiCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI ROW 2 — Orders & Earnings ── */}
        <Grid container spacing={2.5} mb={4}>
          {[
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#6C63FF", fontSize: 26 }} />
              ),
              label: "Total Orders",
              value: orderSummary?.total?.toLocaleString(),
              sub: `+${orderSummary?.today || 0} today`,
            },
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#f59e0b", fontSize: 26 }} />
              ),
              label: "Processing",
              value: orderSummary?.processing?.toLocaleString(),
              bg: "#fffbeb",
              subColor: "#f59e0b",
            },
            {
              icon: (
                <LocalShippingIcon sx={{ color: "#3b82f6", fontSize: 26 }} />
              ),
              label: "Shipped",
              value: orderSummary?.shipped?.toLocaleString(),
              bg: "#eff6ff",
              subColor: "#3b82f6",
            },
            {
              icon: <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 26 }} />,
              label: "Delivered",
              value: orderSummary?.delivered?.toLocaleString(),
              bg: "#f0fdf4",
            },
            {
              icon: <CancelIcon sx={{ color: "#ef4444", fontSize: 26 }} />,
              label: "Cancelled",
              value: orderSummary?.cancelled?.toLocaleString(),
              bg: "#fef2f2",
              subColor: "#ef4444",
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#6C63FF", fontSize: 26 }} />
              ),
              label: "Total Earning",
              value: `₹${(orderSummary?.totalEarn || 0).toLocaleString()}`,
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#22c55e", fontSize: 26 }} />
              ),
              label: "Today Earning",
              value: `₹${(orderSummary?.todayEarn || 0).toLocaleString()}`,
              bg: "#f0fdf4",
            },
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#43C6AC", fontSize: 26 }} />
              ),
              label: "COD Orders",
              value: orderSummary?.cod?.toLocaleString(),
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* ── EARNINGS LINE CHART ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Monthly Earnings & Order Volume</SC>
          <Box sx={{ height: 300 }}>
            <Line data={earningLineData} options={earningLineOpts} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {[
              {
                label: "Total Earning",
                value: `₹${(orderSummary?.totalEarn || 0).toLocaleString()}`,
                color: "#6C63FF",
              },
              {
                label: "Today Earning",
                value: `₹${(orderSummary?.todayEarn || 0).toLocaleString()}`,
                color: "#FF6584",
              },
              {
                label: "Total Orders",
                value: (orderSummary?.total || 0).toLocaleString(),
                color: "#43C6AC",
              },
              {
                label: "Today Orders",
                value: (orderSummary?.today || 0).toLocaleString(),
                color: "#f59e0b",
              },
            ].map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box
                  sx={{
                    background: "#f0eeff",
                    borderRadius: 2,
                    p: 1.5,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: s.color,
                      mx: "auto",
                      mb: 0.5,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {s.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#442141">
                    {s.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── STATUS BAR CHART + DOUGHNUTS ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SC>Monthly Order Status Breakdown</SC>
              <Box sx={{ height: 280 }}>
                <Bar
                  data={statusBarData}
                  options={{
                    ...chartOpts(theme),
                    scales: {
                      ...chartOpts(theme).scales,
                      x: { ...chartOpts(theme).scales.x, stacked: true },
                      y: { ...chartOpts(theme).scales.y, stacked: true },
                    },
                  }}
                />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SC>Order Status Split</SC>
              <Box sx={{ height: 200 }}>
                <Doughnut
                  data={orderStatusDoughnutData}
                  options={doughnutOpts}
                />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["Processing", orderSummary?.processing, "#f59e0b"],
                ["Shipped", orderSummary?.shipped, "#3b82f6"],
                ["Delivered", orderSummary?.delivered, "#22c55e"],
                ["Cancelled", orderSummary?.cancelled, "#ef4444"],
              ].map(([k, v, c]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.4}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: c,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {k}
                    </Typography>
                  </Box>
                  <Chip
                    label={v || 0}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SC>Payment Method Split</SC>
              <Box sx={{ height: 200 }}>
                <Doughnut data={paymentDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["COD", orderSummary?.cod, "#6C63FF"],
                ["Online", orderSummary?.online, "#43C6AC"],
              ].map(([k, v, c]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.4}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: c,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {k}
                    </Typography>
                  </Box>
                  <Chip
                    label={v || 0}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  background: "#f0eeff",
                  borderRadius: 2,
                  p: 1.2,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Online Orders
                </Typography>
                <Typography variant="h6" fontWeight={700} color="#6C63FF">
                  {orderSummary?.online || 0}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* ── CATALOG COUNTS ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Catalog Overview</SC>
          <Grid container spacing={2}>
            {[
              {
                label: "Shop Categories",
                value: catalogCounts?.categories,
                color: "#6C63FF",
                icon: <CategoryIcon sx={{ color: "#6C63FF" }} />,
              },
              {
                label: "Sub-Categories",
                value: catalogCounts?.subCategories,
                color: "#FF6584",
                icon: <LayersIcon sx={{ color: "#FF6584" }} />,
              },
              {
                label: "Total Products",
                value: catalogCounts?.totalProducts,
                color: "#43C6AC",
                icon: <InventoryIcon sx={{ color: "#43C6AC" }} />,
              },
              {
                label: "Available Products",
                value: catalogCounts?.availableProducts,
                color: "#22c55e",
                icon: <StorefrontIcon sx={{ color: "#22c55e" }} />,
              },
              {
                label: "Deleted Products",
                value: catalogCounts?.deletedProducts,
                color: "#94a3b8",
                icon: <InventoryIcon sx={{ color: "#94a3b8" }} />,
              },
            ].map((s, i) => (
              <Grid item xs={12} sm={6} md={2.4} key={i}>
                <Box
                  sx={{
                    background: "#f0eeff",
                    borderRadius: 3,
                    p: 2.5,
                    textAlign: "center",
                    border: `2px solid ${s.color}22`,
                  }}
                >
                  <Box sx={{ mb: 1 }}>{s.icon}</Box>
                  <Typography variant="h3" fontWeight={800} color={s.color}>
                    {s.value || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── CATEGORY PRODUCT COUNT TABLE ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Products per Category</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "5%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <TH
                cols={[
                  "#",
                  "Category",
                  "Total Products",
                  "Available",
                  "Availability %",
                ]}
              />
              <TableBody>
                {categoryProductCount?.map((c, i) => {
                  const pct =
                    c.total > 0 ? Math.round((c.available / c.total) * 100) : 0;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2">{i + 1}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {c.categoryName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Chip label={c.total} size="small" color="primary" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Chip label={c.available} size="small" color="success" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              flex: 1, height: 6, borderRadius: 3,
                              "& .MuiLinearProgress-bar": {
                                background: pct > 70 ? "#22c55e" : pct > 40 ? "#f59e0b" : "#ef4444",
                              },
                            }}
                          />
                          <Typography variant="caption" fontWeight={700}>{pct}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Latest E-Commerce Orders (Last 10)</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <TH
                cols={[
                  "Customer",
                  "Amount",
                  "Grand Total",
                  "Delivery",
                  "Discount",
                  "Payment",
                  "Pay Status",
                  "Order Status",
                ]}
              />
              <TableBody>
                {latestOrders?.map((o, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, flexShrink: 0, background: "#6C63FF" }}>
                          {o.user?.name?.[0] || "G"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {o.user?.name || "Guest"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2">₹{(o.totalAmount || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" fontWeight={700}>₹{(o.grandTotal || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2">₹{(o.deliveryCharges || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2">₹{(o.discount || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}><Chip label={(o.paymentMethod || "").toUpperCase()} size="small" variant="outlined" sx={{ fontSize: 11 }} /></TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>{StatusChip(o.paymentStatus)}</TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>{StatusChip(o.orderStatus)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── PRODUCT STOCK AVAILABILITY — CHART + TABLE ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Product Stock Availability (Sorted by Stock — Low First)</SC>

          {/* ── Stock health summary tiles ── */}
          {(() => {
            const outOfStock =
              productStockTable?.filter((p) => p.totalStock === 0).length || 0;
            const lowStock =
              productStockTable?.filter(
                (p) => p.totalStock > 0 && p.totalStock < 10,
              ).length || 0;
            const healthy =
              productStockTable?.filter((p) => p.totalStock >= 10).length || 0;
            const totalUnits =
              productStockTable?.reduce((s, p) => s + (p.totalStock || 0), 0) ||
              0;
            return (
              <Grid container spacing={2} mb={3}>
                {[
                  {
                    label: "Out of Stock",
                    value: outOfStock,
                    color: "#ef4444",
                    bg: "#fef2f2",
                  },
                  {
                    label: "Low Stock (<10)",
                    value: lowStock,
                    color: "#f59e0b",
                    bg: "#fffbeb",
                  },
                  {
                    label: "Healthy Stock",
                    value: healthy,
                    color: "#22c55e",
                    bg: "#f0fdf4",
                  },
                  {
                    label: "Total Units",
                    value: totalUnits.toLocaleString(),
                    color: "#6C63FF",
                    bg: "#f0eeff",
                  },
                ].map((s, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Box
                      sx={{
                        background: s.bg,
                        borderRadius: 2.5,
                        p: 2,
                        textAlign: "center",
                        border: `1.5px solid ${s.color}33`,
                      }}
                    >
                      <Typography variant="h4" fontWeight={800} color={s.color}>
                        {s.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {s.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            );
          })()}

          {/* ── Horizontal bar chart — top 20 by stock ── */}
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={7}>
              <Box sx={{ background: "#fafafa", borderRadius: 2, p: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#442141"
                  mb={1.5}
                >
                  Stock per Product (Top 20)
                </Typography>
                <Box
                  sx={{
                    height: Math.max(
                      320,
                      Math.min(
                        (productStockTable?.slice(0, 20).length || 10) * 28,
                        560,
                      ),
                    ),
                    overflowY: "auto",
                  }}
                >
                  <Bar
                    data={{
                      labels:
                        productStockTable
                          ?.slice(0, 20)
                          .map((p) =>
                            p.name?.length > 18
                              ? p.name.slice(0, 18) + "…"
                              : p.name,
                          ) || [],
                      datasets: [
                        {
                          label: "Total Stock",
                          data:
                            productStockTable
                              ?.slice(0, 20)
                              .map((p) => p.totalStock) || [],
                          backgroundColor:
                            productStockTable
                              ?.slice(0, 20)
                              .map((p) =>
                                p.totalStock === 0
                                  ? "rgba(239,68,68,0.8)"
                                  : p.totalStock < 10
                                  ? "rgba(245,158,11,0.8)"
                                  : "rgba(34,197,94,0.8)",
                              ) || [],
                          borderRadius: 4,
                          barThickness: 18,
                        },
                      ],
                    }}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              const p = productStockTable?.[ctx.dataIndex];
                              return [
                                ` Stock: ${ctx.raw}`,
                                ` Category: ${p?.categoryName || "—"}`,
                                ` Price: ${
                                  p?.minPrice === p?.maxPrice
                                    ? `₹${p?.minPrice}`
                                    : `₹${p?.minPrice}–₹${p?.maxPrice}`
                                }`,
                              ];
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: {
                            color: theme.palette.text.secondary,
                            font: { size: 10 },
                          },
                          grid: { color: theme.palette.divider },
                        },
                        y: {
                          ticks: {
                            color: theme.palette.text.secondary,
                            font: { size: 10 },
                          },
                          grid: { display: false },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* ── Stock zone doughnut ── */}
            <Grid item xs={12} md={5}>
              <Grid container spacing={2} sx={{ height: "100%" }}>
                <Grid item xs={12}>
                  <Box sx={{ background: "#fafafa", borderRadius: 2, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#442141"
                      mb={1}
                    >
                      Stock Health Distribution
                    </Typography>
                    <Box sx={{ height: 200 }}>
                      <Doughnut
                        data={{
                          labels: [
                            "Out of Stock",
                            "Low Stock (<10)",
                            "Healthy (≥10)",
                          ],
                          datasets: [
                            {
                              data: [
                                productStockTable?.filter(
                                  (p) => p.totalStock === 0,
                                ).length || 0,
                                productStockTable?.filter(
                                  (p) => p.totalStock > 0 && p.totalStock < 10,
                                ).length || 0,
                                productStockTable?.filter(
                                  (p) => p.totalStock >= 10,
                                ).length || 0,
                              ],
                              backgroundColor: [
                                "#ef4444",
                                "#f59e0b",
                                "#22c55e",
                              ],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                              labels: { font: { size: 11 } },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ background: "#fafafa", borderRadius: 2, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#442141"
                      mb={1}
                    >
                      Availability Status
                    </Typography>
                    <Box sx={{ height: 160 }}>
                      <Doughnut
                        data={{
                          labels: ["Available", "Unavailable"],
                          datasets: [
                            {
                              data: [
                                productStockTable?.filter((p) => p.isAvailable)
                                  .length || 0,
                                productStockTable?.filter((p) => !p.isAvailable)
                                  .length || 0,
                              ],
                              backgroundColor: ["#6C63FF", "#94a3b8"],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "bottom",
                              labels: { font: { size: 11 } },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* ── Product stock detail table ── */}
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "9%" }} />
              </colgroup>
              <TH
                cols={[
                  "#",
                  "Product",
                  "Category",
                  "Sub-Category",
                  "Vars",
                  "Stock",
                  "Price Range",
                  "Status",
                ]}
              />
              <TableBody>
                {productStockTable?.map((p, i) => {
                  const stockColor = p.totalStock === 0 ? "#ef4444" : p.totalStock < 10 ? "#f59e0b" : "#22c55e";
                  const stockBg = p.totalStock === 0 ? "#fef2f2" : p.totalStock < 10 ? "#fffbeb" : "#f0fdf4";
                  return (
                    <TableRow key={i} hover sx={{ "&:hover": { background: "#fafafa" } }}>
                      <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" color="text.secondary">{i + 1}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={p.thumbnail} variant="rounded" sx={{ width: 28, height: 28, flexShrink: 0, background: "#f0eeff", fontSize: 10 }}>
                            {p.name?.[0] || "P"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} noWrap>{p.name || "—"}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {p.productFor === "beautician" ? "Beautician" : "User"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" noWrap>{p.categoryName || "—"}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" noWrap>{p.subCategoryName || "—"}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}><Chip label={p.variationCount || 0} size="small" variant="outlined" sx={{ fontSize: 11 }} /></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box sx={{ background: stockBg, borderRadius: 1.5, px: 1, py: 0.4, display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: stockColor, flexShrink: 0 }} />
                          <Typography variant="caption" fontWeight={800} color={stockColor}>{p.totalStock}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {p.totalStock === 0 ? "Out" : p.totalStock < 10 ? "Low" : "OK"}
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.min((p.totalStock / maxStock) * 100, 100)}
                          sx={{ mt: 0.5, height: 4, borderRadius: 2, "& .MuiLinearProgress-bar": { background: stockColor } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" noWrap>
                          {p.minPrice === p.maxPrice ? `₹${p.minPrice}` : `₹${p.minPrice}–₹${p.maxPrice}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Chip label={p.isAvailable ? "On" : "Off"} size="small" color={p.isAvailable ? "success" : "error"} sx={{ fontWeight: 700, fontSize: 11 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── LATEST 10 E-COMMERCE ORDERS ── */}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EcommerceDashboard;
